import {
  ArrowBendUpLeftIcon,
  ArrowsLeftRightIcon,
  BellIcon,
  CalendarCheckIcon,
  ChatCenteredTextIcon,
  ChatCircleDotsIcon,
  CheckSquareOffsetIcon,
  ClipboardTextIcon,
  ClockCountdownIcon,
  FunnelIcon,
  GitBranchIcon,
  HourglassIcon,
  KanbanIcon,
  ListChecksIcon,
  NoteIcon,
  PencilSimpleIcon,
  PhoneCallIcon,
  SignOutIcon,
  SparkleIcon,
  TagIcon,
  UserCirclePlusIcon,
} from '@phosphor-icons/react'
import type { Icon } from '@phosphor-icons/react'
import type { ListCopy } from 'lib/listCopy'
import { ObjectId } from 'lib/ObjectId'
import { DEFAULT_PAPERWORK_EXPIRY_DAYS, DEFAULT_PAPERWORK_MESSAGE } from './paperworkStep'
import type { WorkflowNodeData, WorkflowNodeKind } from 'app/types/workflows'
import { NEW_TEXT_CONVERSATION, type TriggerKind } from './triggerCatalog'

/** A draggable block in the left palette. `defaults` is the node data created on drop. */
export type PaletteItem = {
  kind: WorkflowNodeKind
  icon: Icon
  chip: string
  label: string
  description: string
  defaults: WorkflowNodeData
}

export type PaletteGroup = {
  title: string
  items: PaletteItem[]
}

/** Grayscale icon chip — blocks are distinguished by icon, not colour. */
const CHIP = `bg-surface-muted text-subtle`

// Triggers are not draggable blocks — they're chosen once via the trigger modal when a
// workflow is created, then edited in the inspector. The palette is body steps only.
export const paletteGroups = (copy: ListCopy): PaletteGroup[] => [
  {
    title: `Contact`,
    items: [
      {
        kind: `createTask`,
        icon: ListChecksIcon,
        chip: CHIP,
        label: `Create task`,
        description: `Add a task for the team`,
        defaults: { label: `Create task`, title: ``, ownerId: `contact-owner`, dueType: `relative`, dueInDays: 3 },
      },
      {
        kind: `editTask`,
        icon: CheckSquareOffsetIcon,
        chip: CHIP,
        label: `Edit task`,
        description: `Close, archive or reassign the task above`,
        defaults: { label: `Edit task`, onlyIfOpen: true },
      },
      {
        kind: `addNote`,
        icon: NoteIcon,
        chip: CHIP,
        label: `Add note`,
        description: `Log a note on the contact`,
        defaults: { label: `Add note`, body: `` },
      },
      {
        kind: `logFollowUp`,
        icon: CalendarCheckIcon,
        chip: CHIP,
        label: `Log a follow-up`,
        description: `Log a follow-up on the contact, like the Follow Up action`,
        defaults: {
          label: `Log a follow-up`,
          connected: `fromDirection`,
          dateSource: `event`,
          note: `{{activity.commLogChannelLabel}}: {{activity.commLogSubject}} {{activity.commLogNote}}`,
        },
      },
      {
        // "Add or remove tags", not "Tag object": nothing else in the app says "object", and the
        // front desk doesn't either (UX audit 08-21, C7).
        kind: `tagObject`,
        icon: TagIcon,
        chip: CHIP,
        label: `Add or remove tags`,
        description: `Apply or clear a tag on the contact`,
        defaults: { label: `Add or remove tags`, target: `contact`, operation: `add`, tags: [] },
      },
      {
        kind: `editObject`,
        icon: PencilSimpleIcon,
        chip: CHIP,
        label: `Update contact`,
        description: `Set one or more fields on the contact`,
        defaults: { label: `Update contact`, target: `contact`, fields: [{ operation: `set`, value: `` }] },
      },
      {
        kind: `updateAppointment`,
        icon: CalendarCheckIcon,
        chip: CHIP,
        label: `Confirm appointment`,
        description: `Mark it confirmed in your practice system`,
        defaults: { label: `Confirm appointment` },
      },
      {
        kind: `assignOwner`,
        icon: UserCirclePlusIcon,
        chip: CHIP,
        label: `Assign owner`,
        description: `Set the contact's owner`,
        defaults: { label: `Assign owner` },
      },
      {
        kind: `addToPipeline`,
        icon: KanbanIcon,
        chip: CHIP,
        label: copy.addToList,
        description: copy.placeContactInList,
        defaults: { label: copy.addToList, operation: `add` },
      },
      {
        kind: `addToPipeline`,
        icon: ArrowsLeftRightIcon,
        chip: CHIP,
        label: copy.moveListStage,
        description: `Move the contact to a stage`,
        defaults: { label: copy.moveListStage, operation: `move` },
      },
    ],
  },
  {
    title: `Conversation`,
    items: [
      {
        kind: `sendSms`,
        icon: ChatCircleDotsIcon,
        chip: CHIP,
        label: `Send SMS`,
        description: `Text the contact`,
        defaults: { label: `Send SMS`, toField: `cellPhone`, message: `` },
      },
      {
        kind: `sendPaperwork`,
        icon: ClipboardTextIcon,
        chip: CHIP,
        label: `Send paperwork`,
        description: `Text the patient a paperwork link`,
        defaults: {
          label: `Send paperwork`,
          expiresInDays: DEFAULT_PAPERWORK_EXPIRY_DAYS,
          message: DEFAULT_PAPERWORK_MESSAGE,
          toField: `cellPhone`,
        },
      },
      {
        kind: `aiAgentSms`,
        icon: SparkleIcon,
        chip: CHIP,
        label: `Hand off to AI agent`,
        description: `Let the agent take the conversation`,
        defaults: {
          label: `Hand off to AI agent`,
          toField: `cellPhone`,
          message: ``,
          brief: ``,
          whenBusy: `skip`,
        },
      },
      {
        kind: `initiateCall`,
        icon: PhoneCallIcon,
        chip: CHIP,
        label: `Initiate call`,
        description: `Ring a number, bridge to the contact`,
        defaults: { label: `Initiate call`, ringNumber: `` },
      },
      {
        kind: `waitForReply`,
        icon: ArrowBendUpLeftIcon,
        chip: CHIP,
        label: `Wait for reply`,
        description: `Fork on whether they reply`,
        defaults: { label: `Wait for reply`, within: 1, unit: `days` },
      },
      {
        kind: `waitForKeyword`,
        icon: ChatCenteredTextIcon,
        chip: CHIP,
        label: `Wait for a keyword`,
        description: `Fork on what they reply`,
        // paletteGroups runs on every picker render, so each new step gets its own option id.
        defaults: {
          label: `Wait for a keyword`,
          options: [{ id: ObjectId(), label: ``, keywords: [] }],
          within: 1,
          unit: `days`,
        },
      },
      {
        kind: `waitForCallResult`,
        icon: PhoneCallIcon,
        chip: CHIP,
        label: `Wait for call result`,
        description: `Fork on how the call went`,
        defaults: { label: `Wait for call result` },
      },
    ],
  },
  {
    title: `Team`,
    items: [
      {
        kind: `sendNotification`,
        icon: BellIcon,
        chip: CHIP,
        label: `Send notification`,
        description: `Email or in-app alert`,
        defaults: { label: `Send notification`, channel: `email`, recipients: [], message: `` },
      },
    ],
  },
  {
    title: `Timing`,
    items: [
      {
        kind: `wait`,
        icon: HourglassIcon,
        chip: CHIP,
        label: `Wait`,
        description: `Pause before the next step`,
        defaults: { label: `Wait`, duration: 1, unit: `days`, businessHoursOnly: false },
      },
      {
        kind: `waitUntil`,
        icon: ClockCountdownIcon,
        chip: CHIP,
        label: `Wait until`,
        description: `Wait for a change, up to a time limit`,
        defaults: {
          label: `Wait until`,
          duration: 30,
          unit: `days`,
          match: `all`,
          conditions: [{ type: `field`, operator: `is` }],
        },
      },
    ],
  },
  {
    title: `Logic`,
    items: [
      {
        kind: `filter`,
        icon: FunnelIcon,
        chip: CHIP,
        label: `Filter`,
        description: `Continue only if conditions match`,
        defaults: { label: `Filter`, match: `all`, conditions: [{ type: `field`, operator: `is` }] },
      },
      {
        kind: `branch`,
        icon: GitBranchIcon,
        chip: CHIP,
        label: `Branch`,
        description: `Split on a property (yes / no)`,
        defaults: { label: `Branch`, match: `all`, conditions: [{ type: `field`, operator: `is` }] },
      },
    ],
  },
  {
    title: `Exit`,
    items: [
      {
        kind: `exit`,
        icon: SignOutIcon,
        chip: CHIP,
        label: `Exit`,
        description: `Contact leaves the workflow`,
        defaults: { label: `Exit` },
      },
    ],
  },
]

/** One icon per block kind, so the canvas, the palette and the inspector all show the same mark for
 *  the same step. The node components still pass their own icon (some vary it by state, e.g. move vs
 *  add on a pipeline block); this is the lookup for everywhere that only has a kind to go on. The
 *  trigger's icon depends on which trigger was chosen, so callers pass that one in. */
const TEXTING_STEPS: ReadonlySet<WorkflowNodeKind> = new Set([`sendSms`, `aiAgentSms`])

/** A new step's starting data. After a New text conversation the only number worth texting is the
 *  one that texted in, so texting steps start there instead of on the contact's cell phone. */
export function defaultsForTrigger(item: PaletteItem, triggerKind: TriggerKind | undefined): WorkflowNodeData {
  if (triggerKind !== NEW_TEXT_CONVERSATION || !TEXTING_STEPS.has(item.kind)) return item.defaults
  return { ...item.defaults, toField: `fromNumber` } as WorkflowNodeData
}

export const NODE_ICON: Record<Exclude<WorkflowNodeKind, `trigger`>, Icon> = {
  sendSms: ChatCircleDotsIcon,
  aiAgentSms: SparkleIcon,
  initiateCall: PhoneCallIcon,
  createTask: ListChecksIcon,
  editTask: CheckSquareOffsetIcon,
  tagObject: TagIcon,
  addToPipeline: KanbanIcon,
  editObject: PencilSimpleIcon,
  updateAppointment: CalendarCheckIcon,
  assignOwner: UserCirclePlusIcon,
  addNote: NoteIcon,
  logFollowUp: CalendarCheckIcon,
  sendPaperwork: ClipboardTextIcon,
  sendNotification: BellIcon,
  wait: HourglassIcon,
  waitUntil: ClockCountdownIcon,
  filter: FunnelIcon,
  waitForReply: ArrowBendUpLeftIcon,
  waitForKeyword: ChatCenteredTextIcon,
  waitForCallResult: PhoneCallIcon,
  branch: GitBranchIcon,
  exit: SignOutIcon,
}
